import { Badge, Box, Flex, Grid, Text } from '@radix-ui/themes'
import cn from 'classnames'
import { MILESTONES, milestoneLabel, type DeliveryVerdict, type OrgDeliveryHealth } from '../timeline'

const VERDICT: Record<DeliveryVerdict, { label: string; color: 'gray' | 'green' | 'amber' | 'red' }> = {
  not_started: { label: 'Nothing to deliver', color: 'gray' },
  on_track: { label: 'On track', color: 'green' },
  needs_attention: { label: 'Needs attention', color: 'amber' },
  at_risk: { label: 'At risk', color: 'red' },
  complete: { label: 'Complete', color: 'green' }
}

interface TileProps {
  label: string
  value: string
  detail?: string
  color?: 'gray' | 'green' | 'amber' | 'red'
  children?: React.ReactNode
}

const Tile = ({ label, value, detail, color = 'gray', children }: TileProps) => (
  <Flex direction="column" gap="1" p="3" className="rounded-[var(--radius-3)] bg-[var(--gray-a2)]">
    <Text size="1" color="gray">
      {label}
    </Text>
    <Text size="6" weight="bold" color={color === 'gray' ? undefined : color}>
      {value}
    </Text>
    {detail && (
      <Text size="1" color="gray">
        {detail}
      </Text>
    )}
    {children}
  </Flex>
)

const days = (value: number | null): string => (value === null ? '—' : `${value}d`)

// Reads worse as the backlog ages: a year-old open installation is not a gray statistic.
const openAgeColor = (value: number | null): 'gray' | 'amber' | 'red' => {
  if (value === null) return 'gray'
  if (value > 180) return 'red'
  if (value > 90) return 'amber'
  return 'gray'
}

const blockedDetail = (health: OrgDeliveryHealth): string => {
  const parts = [
    health.stalledCount > 0 ? `${health.stalledCount} stalled` : null,
    health.overdueCount > 0 ? `${health.overdueCount} overdue` : null
  ].filter((part) => part !== null)
  return parts.length === 0 ? 'nothing blocked' : parts.join(' · ')
}

// Spells out the denominator, which counts never-installed work as having missed its date.
const onTimeDetail = (health: OrgDeliveryHealth): string =>
  [
    `${health.onTimeCount} on time`,
    health.lateCount > 0 ? `${health.lateCount} late` : null,
    health.overdueCount > 0 ? `${health.overdueCount} still open past plan` : null,
    health.medianSlipDays !== null
      ? `median slip ${health.medianSlipDays > 0 ? '+' : ''}${health.medianSlipDays}d`
      : null
  ]
    .filter((part) => part !== null)
    .join(' · ')

export const DeliveryHealthPanel = ({ health }: { health: OrgDeliveryHealth }) => {
  const verdict = VERDICT[health.verdict]
  const steps = MILESTONES.filter((milestone) => health.medianStepDays[milestone] !== undefined)

  return (
    <Flex direction="column" gap="3">
      <Flex align="center" gap="2" wrap="wrap">
        <Badge color={verdict.color} variant="soft" size="2">
          {verdict.label}
        </Badge>
        {health.blockedCount > 0 && (
          <Text size="1" color="gray">
            {Math.round(health.blockedShare * 100)}% of outstanding work is blocked
          </Text>
        )}
      </Flex>

      <Grid columns={{ initial: '2', sm: '4' }} gap="3">
        <Tile
          label="Progress"
          value={`${health.installed}/${health.total}`}
          detail={`${health.installedPercent}% installed`}
        >
          <Box className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-[var(--gray-a4)]">
            <Box
              className={cn(
                'h-full',
                health.installedPercent === 100 ? 'bg-[var(--green-9)]' : 'bg-[var(--accent-9)]'
              )}
              style={{ width: `${health.installedPercent}%` }}
            />
          </Box>
        </Tile>

        <Tile
          label="Blocked"
          value={String(health.blockedCount)}
          detail={blockedDetail(health)}
          color={health.blockedCount === 0 ? 'gray' : verdict.color}
        />

        <Tile
          label="Cycle time"
          value={days(health.medianCycleDays)}
          detail={health.medianCycleDays === null ? 'nothing installed yet' : 'median created → installed'}
        />

        <Tile
          label="Oldest open"
          value={days(health.oldestOpenAgeDays)}
          detail={
            health.medianOpenAgeDays === null ? 'no open installations' : `median ${health.medianOpenAgeDays}d`
          }
          color={openAgeColor(health.oldestOpenAgeDays)}
        />
      </Grid>

      {health.onTimeRate !== null && (
        <Text size="2" color="gray">
          {health.onTimeRate}% met the planned date · {onTimeDetail(health)}
        </Text>
      )}

      {steps.length > 0 && (
        <Flex direction="column" gap="1">
          <Text size="1" color="gray">
            Median days per step
          </Text>
          <Flex gap="3" wrap="wrap">
            {steps.map((milestone) => (
              <Text key={milestone} size="1">
                {milestoneLabel[milestone]} <Text color="gray">{health.medianStepDays[milestone]}d</Text>
              </Text>
            ))}
          </Flex>
        </Flex>
      )}
    </Flex>
  )
}
